import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import './Contact.css';

// `label` (English) is what gets sent to the email API; `labelKey` is display-only.
const subjectOptions = [
  { value: '', label: 'Select a topic...', labelKey: 'agency.contact_subject_placeholder' },
  { value: 'training', label: 'Training & Certifications', labelKey: 'agency.contact_subject_training_option' },
  { value: 'staffing', label: 'Staff Augmentation', labelKey: 'agency.contact_staff_augmentation_label' },
  { value: 'it-services', label: 'IT Services', labelKey: 'agency.contact_subject_it_services_option' },
  { value: 'careers', label: 'Career Opportunities', labelKey: 'agency.contact_career_opportunities_label' },
  { value: 'general', label: 'General Inquiry', labelKey: 'agency.contact_subject_general_option' },
];

function CustomSelect({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = subjectOptions.find(o => o.value === value) || subjectOptions[0];

  return (
    <div className={`custom-select${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className="custom-select-trigger" onClick={() => setOpen(v => !v)}>
        <span className={value ? '' : 'placeholder'}>{t(selected.labelKey, selected.label)}</span>
        <svg className="custom-select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <ul className="custom-select-list">
          {subjectOptions.map(opt => (
            <li
              key={opt.value}
              className={`custom-select-option${opt.value === value ? ' selected' : ''}${opt.value === '' ? ' placeholder-opt' : ''}`}
              onMouseDown={() => { onChange(opt.value); setOpen(false); }}
            >
              {t(opt.labelKey, opt.label)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const IconGradCap = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c3.33 1.67 8.67 1.67 12 0v-5"/>
  </svg>
);
const IconUsers2 = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const IconShield = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconBriefcase = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="12"/>
    <path d="M2 12h20"/>
  </svg>
);

const EMAIL_API_URL = 'https://emailer.lts251.com/email-thirdeye.php';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '', subscribe: false });
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('agency.contact_name_required_error', 'Name is required');
    if (!form.email.trim()) e.email = t('agency.contact_email_required_error', 'Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('agency.contact_email_invalid_error', 'Enter a valid email');
    if (!form.message.trim()) e.message = t('agency.contact_message_required_error', 'Message is required');
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    const oversized = files.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) errs.file = t('agency.contact_file_too_large_error', '"{name}" exceeds the 10MB size limit.', { name: oversized.name });
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitError('');

    const subjectLabel = subjectOptions.find(o => o.value === form.subject)?.label;

    const data = new FormData();
    data.append('name', form.name);
    data.append('email', form.email);
    data.append('message', form.message);
    if (form.company.trim()) data.append('company', form.company);
    if (form.phone.trim()) data.append('phone', form.phone);
    if (form.subject && subjectLabel) data.append('subject', subjectLabel);
    if (form.subscribe) data.append('newsletter', '1');
    files.forEach(f => data.append('attachments', f));

    setSubmitting(true);
    try {
      const res = await fetch(EMAIL_API_URL, { method: 'POST', body: data });
      const result = await res.json().catch(() => null);
      if (res.ok && result?.status === 'success') {
        setSubmitted(true);
      } else {
        setSubmitError(result?.message || t('agency.contact_send_failed_error', 'We could not send your message right now. Please try again later.'));
      }
    } catch (err) {
      setSubmitError(t('agency.contact_network_error', 'We could not reach the server. Please check your connection and try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
    if (errors.file) setErrors(prev => ({ ...prev, file: '' }));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">{t('agency.contact_hero_badge', 'Get In Touch')}</span>
          <h1>{t('agency.contact_hero_title', 'Let Us Start a')} <span style={{ color: '#d5772d' }}>{t('agency.contact_hero_highlight_title', 'Conversation')}</span></h1>
          <p>{t('agency.contact_hero_desc', 'Whether you need training, staffing solutions, or IT services - we would love to hear about your challenges and how we can help.')}</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">

            {/* Info Panel */}
            <div className="contact-info">
              <h2>{t('agency.contact_info_title', 'How Can We Help?')}</h2>
              <p>{t('agency.contact_info_desc', 'Tell us about your cybersecurity needs and one of our experts will get back to you within one business day.')}</p>

              <div className="contact-options">
                {[
                  { icon: <IconGradCap />, title: t('agency.contact_training_inquiries_title', 'Training Inquiries'), desc: t('agency.contact_training_inquiries_desc', 'Ask about course schedules, custom training programs, and group pricing.') },
                  { icon: <IconUsers2 />, title: t('agency.contact_staff_augmentation_label', 'Staff Augmentation'), desc: t('agency.contact_staff_augmentation_desc', "Discuss your talent requirements and we will match you with pre-vetted professionals.") },
                  { icon: <IconShield />, title: t('agency.contact_it_security_services_title', 'IT & Security Services'), desc: t('agency.contact_it_security_services_desc', 'Get a consultation on your security posture, cloud needs, or IT infrastructure.') },
                  { icon: <IconBriefcase />, title: t('agency.contact_career_opportunities_label', 'Career Opportunities'), desc: t('agency.contact_career_opportunities_desc', "Interested in joining our team? Send your resume and we will be in touch.") },
                ].map((opt, i) => (
                  <div className="contact-option" key={i}>
                    <div className="contact-opt-icon">{opt.icon}</div>
                    <div>
                      <h4>{opt.title}</h4>
                      <p>{opt.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="contact-socials">
                <p className="socials-label">{t('agency.contact_connect_label', 'Connect with us')}</p>
                <div className="socials-row">
                  {[
                    { label: 'Facebook', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg> },
                    { label: 'Twitter', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg> },
                    { label: 'Instagram', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg> },
                    { label: 'LinkedIn', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg> },
                  ].map(s => (
                    <a key={s.label} href="#!" className="contact-social" aria-label={s.label}>{s.icon}</a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="contact-form-wrap">
              {submitted ? (
                <div className="form-success">
                  <div className="success-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <h3>{t('agency.contact_success_title', 'Message Sent!')}</h3>
                  <p>{t('agency.contact_success_msg', 'Thank you for reaching out. A member of our team will get back to you within one business day.')}</p>
                  <button className="btn-primary" onClick={() => { setSubmitted(false); setForm({ name: '', email: '', company: '', phone: '', subject: '', message: '', subscribe: false }); setFiles([]); }} style={{ marginTop: '24px' }}>
                    {t('agency.contact_send_another_btn', 'Send Another Message')}
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  <h3 className="form-title">{t('agency.contact_form_title', 'Send Us a Message')}</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">{t('agency.contact_full_name_label', 'Full Name')} <span className="req">*</span></label>
                      <input
                        id="name" name="name" type="text"
                        placeholder={t('agency.contact_name_placeholder', 'John Smith')}
                        value={form.name} onChange={handleChange}
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && <span className="form-error">{errors.name}</span>}
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">{t('agency.contact_email_label', 'Email Address')} <span className="req">*</span></label>
                      <input
                        id="email" name="email" type="email"
                        placeholder="john@company.com"
                        value={form.email} onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="form-error">{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="company">{t('agency.contact_company_label', 'Company')}</label>
                      <input
                        id="company" name="company" type="text"
                        placeholder={t('agency.contact_company_placeholder', 'Acme Inc')}
                        value={form.company} onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">{t('agency.contact_phone_label', 'Phone')}</label>
                      <input
                        id="phone" name="phone" type="tel"
                        placeholder="(555) 123-4567"
                        value={form.phone} onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t('agency.contact_subject_label', 'Subject')}</label>
                    <CustomSelect
                      value={form.subject}
                      onChange={(val) => setForm(prev => ({ ...prev, subject: val }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">{t('agency.contact_message_label', 'Message')} <span className="req">*</span></label>
                    <textarea
                      id="message" name="message"
                      placeholder={t('agency.contact_message_placeholder', 'Tell us about your needs, challenges, or questions...')}
                      rows={6}
                      value={form.message} onChange={handleChange}
                      className={errors.message ? 'error' : ''}
                    />
                    {errors.message && <span className="form-error">{errors.message}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="file" className="file-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      {t('agency.contact_attach_files_label', 'Attach Files (optional)')}
                    </label>
                    <input id="file" type="file" multiple className="file-input" onChange={handleFileChange} />
                    <p className="file-hint">{t('agency.contact_file_size_hint_msg', 'Max 10 MB per file')}</p>
                    {errors.file && <span className="form-error">{errors.file}</span>}
                    {files.length > 0 && (
                      <ul className="file-list">
                        {files.map((f, i) => (
                          <li key={`${f.name}-${i}`} className="file-hint">
                            {f.name} ({(f.size / (1024 * 1024)).toFixed(2)} MB)
                            <button type="button" onClick={() => removeFile(i)} style={{ marginLeft: '8px' }}>{t('agency.contact_remove_file_btn', 'Remove')}</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="form-checkbox">
                    <input
                      type="checkbox" id="subscribe" name="subscribe"
                      checked={form.subscribe} onChange={handleChange}
                    />
                    <label htmlFor="subscribe">
                      {t('agency.contact_newsletter_label', 'Sign me up for the ThirdEye newsletter - updates, insights, and promotions.')}
                    </label>
                  </div>

                  {submitError && <p className="form-error" style={{ marginBottom: '16px' }}>{submitError}</p>}

                  <button type="submit" className="btn-primary form-submit" disabled={submitting}>
                    {submitting ? t('agency.contact_sending_btn', 'Sending...') : t('agency.contact_send_message_btn', 'Send Message')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
